// Ask your team: pick a role, say what you want, and watch it get done.
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi } from "@/hooks/useApi";
import { api } from "@/lib/api";
import { usePerm } from "@/lib/perms";
import { useActiveAccount } from "@/state/accountContext";
import { TaskView } from "./TaskView";
import { fmtWhen, orgOrder, TaskStatusBadge, type AccountLite, type AgentsResp, type TaskSummary } from "./shared";

/** The page the person was looking at when they asked (the floating bubble passes it). */
export type AskPage = { path: string; title?: string };

export function AskTab({ data, canRun, preselect, taskId, onTask, compact = false, page }: {
  data: AgentsResp | null; canRun: boolean; preselect: number | null; taskId: number | null; onTask: (id: number | null) => void;
  /** Single-column layout for the slide-out panel: the open request replaces the form, with a Back button. */
  compact?: boolean;
  page?: AskPage | null;
}) {
  const { can } = usePerm();
  const { activeAccountId } = useActiveAccount();
  const canAccounts = can("accounts.data.READ");
  const accounts = useApi<{ rows: AccountLite[] }>(canAccounts ? "/api/accounts?limit=500" : null);
  const list = useApi<{ tasks: TaskSummary[] }>("/api/agents/tasks?limit=30", { refetchInterval: 5000 });
  const ordered = orgOrder(data?.items ?? []);
  const [agentId, setAgentId] = useState<string>("");
  const [shopId, setShopId] = useState<string>(activeAccountId ? String(activeAccountId) : "none");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  // Default to the first top-level role (a head of department); honour "Ask" from the Team tab.
  useEffect(() => {
    if (preselect) setAgentId(String(preselect));
    else if (!agentId && ordered.length) setAgentId(String((ordered.find((o) => o.agent.enabled) ?? ordered[0]).agent.id));
  }, [preselect, ordered.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!text.trim() || !agentId || busy) return;
    setBusy(true);
    try {
      const t = await api.post<{ id: number }>("/api/agents/tasks", {
        agentId: Number(agentId), instructions: text, shopId: shopId === "none" ? null : Number(shopId),
        ...(page?.path ? { page } : {}),
      });
      setText(""); void list.reload(); onTask(t.id);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Couldn't send"); }
    finally { setBusy(false); }
  };

  const capReached = !!data?.spend?.reached;
  const tasks = (list.data?.tasks ?? []).slice(0, compact ? 6 : undefined);

  // Panel mode: one column. An open request takes the whole panel.
  if (compact && taskId) {
    return (
      <div className="space-y-2">
        <Button size="sm" variant="ghost" className="-ml-2 h-7 px-2 text-xs" onClick={() => onTask(null)}>
          <ArrowLeft className="mr-1 size-3.5" />Back
        </Button>
        <TaskView key={taskId} taskId={taskId} canRun={canRun} onChanged={() => void list.reload()} />
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-3" : "grid gap-4 lg:grid-cols-[22rem_1fr]"}>
      <div className="space-y-3">
        {canRun ? (
          <Card className="space-y-2 p-3">
            <p className="text-sm font-medium">Ask your team</p>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Who should handle it?" /></SelectTrigger>
              <SelectContent>
                {ordered.map(({ agent: a, depth }) => (
                  <SelectItem key={a.id} value={String(a.id)}>{"— ".repeat(depth)}{a.name}{a.enabled ? "" : " (off)"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canAccounts && (
              <Select value={shopId} onValueChange={setShopId}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No account (agency-wide)</SelectItem>
                  {(accounts.data?.rows ?? []).map((r) => <SelectItem key={r.id} value={String(r.id)}>{r.company || r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className="text-sm"
              placeholder="e.g. Plan this month's marketing for this client and have your team draft the posts."
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void send(); }} />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">⌘/Ctrl + Enter to send</span>
              <Button size="sm" onClick={send} disabled={busy || !text.trim() || !agentId}>
                {busy ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : <Send className="mr-1 size-3.5" />}Send
              </Button>
            </div>
            {capReached && (
              <p className="text-xs text-destructive">Today's AI spend cap is reached, so new requests will stop straight away until it resets.</p>
            )}
            {page?.path && <p className="truncate text-[11px] text-muted-foreground">Includes the page you're on: {page.title || page.path}</p>}
            {(data?.items.length ?? 0) === 0 && <p className="text-xs text-muted-foreground">No one is on the team yet. Hire roles on the Team tab first.</p>}
          </Card>
        ) : <p className="text-xs text-muted-foreground">You can read requests but not start them.</p>}

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Recent requests</p>
          {tasks.length === 0 && <p className="text-xs text-muted-foreground">Nothing yet.</p>}
          {tasks.map((t) => (
            <button key={t.id} type="button" onClick={() => onTask(t.id)}
              className={`w-full rounded-md border p-2.5 text-left text-sm hover:bg-muted/30 ${taskId === t.id ? "border-primary/60 bg-muted/30" : "border-border"}`}>
              <span className="flex items-start justify-between gap-2">
                <span className="min-w-0 truncate font-medium">{t.title}</span>
                <TaskStatusBadge status={t.status} />
              </span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {t.agent_name}{t.shop_name ? ` · ${t.shop_name}` : ""}{t.subtasks ? ` · ${t.subtasks_done}/${t.subtasks} sub-tasks` : ""} · {fmtWhen(t.created_at)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {!compact && <div>
        {taskId
          ? <TaskView key={taskId} taskId={taskId} canRun={canRun} onChanged={() => void list.reload()} />
          : <Card className="p-8 text-center text-sm text-muted-foreground">Ask a role to do something, or pick a request on the left. You'll see who it was handed to and what came back.</Card>}
      </div>}
    </div>
  );
}
